<template>
  <div :class="['min-h-screen flex flex-col items-center justify-center relative', isDark ? 'dark' : '', isClinicLoading ? 'filter blur-sm pointer-events-none select-none' : '']">
    <!-- Overlay de loading e blur -->
    <div
      v-if="isClinicLoading"
      class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-white/40 dark:bg-gray-900/40 transition-all"
    >
      <svg class="animate-spin h-12 w-12 text-[#006eff]" viewBox="0 0 24 24" fill="none">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/>
      </svg>
    </div>
    <!-- Botão de alternar modo claro/escuro -->
    <button
      @click="toggleDarkMode"
      class="theme-toggle fixed top-4 right-4 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-all duration-300 ease-in-out shadow-lg z-20 backdrop-blur-sm"
      aria-label="Alternar modo escuro"
    >
      <transition name="rotate" mode="out-in">
        <svg v-if="isDark" key="sun" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
        <svg v-else key="moon" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      </transition>
    </button>

    <!-- Background animado -->
    <div class="fixed inset-0 w-full h-full overflow-hidden">
      <canvas ref="waveCanvas" class="absolute inset-0 w-full h-full transition-opacity duration-500"></canvas>
      <div
        class="absolute inset-0 transition-colors duration-500"
        :style="{
          background: isDark
            ? `linear-gradient(to bottom right, ${clinicStore.clinic.cor_gradiente_login_from_dark}CC 0%, ${clinicStore.clinic.cor_gradiente_login_via_dark}80 50%, ${clinicStore.clinic.cor_gradiente_login_to_dark}CC 100%)`
            : `linear-gradient(to bottom right, ${clinicStore.clinic.cor_gradiente_login_from}CC 0%, ${clinicStore.clinic.cor_gradiente_login_via}80 50%, ${clinicStore.clinic.cor_gradiente_login_to}CC 100%)`
        }"
      ></div>
      <div
        class="absolute inset-0"
        :style="{
          background: isDark
            ? `radial-gradient(ellipse at center, transparent 60%, ${clinicStore.clinic.cor_gradiente_login_radial_dark}33 100%)`
            : `radial-gradient(ellipse at center, transparent 60%, ${clinicStore.clinic.cor_gradiente_login_radial}33 100%)`
        }"
      ></div>
    </div>

    <!-- Container principal -->
    <div class="z-10 w-full max-w-6xl px-6 py-8">
      <!-- Card principal -->
      <div class="flex flex-col md:flex-row rounded-3xl overflow-hidden justify-center items-center">
        <!-- Lado direito: Login -->
        <div class="w-full max-w-md bg-white dark:bg-gray-800 p-10 mx-auto rounded-3xl">
          <div class="flex justify-center mb-8 relative">
            <img :src="loginLogoSrc" alt="Logo Login" :style="{ width: '300px', height: '180px', objectFit: 'contain' }" class="mx-auto mb-4" />
            <!-- Ícone de informação -->
            <button
              @click="showVersionModal = true"
              class="absolute -top-2 -right-2 p-2 rounded-full bg-blue-100 dark:bg-blue-900/50 hover:bg-blue-200 dark:hover:bg-blue-800/70 text-blue-600 dark:text-blue-400 transition-all duration-200 hover:scale-110 cursor-pointer"
              aria-label="Informações da versão"
              title="Informações da versão"
            >
              <Info class="w-4 h-4" />
            </button>
          </div>
          
         
          <p class="text-center text-gray-600 dark:text-gray-400 mb-4">
            Acesse o sistema com suas credenciais
          </p>

          <!-- Mensagens -->
          <transition name="message-fade">
            <div v-if="errorMessage" class="message-box error mb-6">
              <svg class="w-5 h-5 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{{ errorMessage }}</span>
            </div>
          </transition>

          <transition name="message-fade">
            <div v-if="successMessage" class="message-box success mb-6">
              <svg class="w-5 h-5 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{{ successMessage }}</span>
            </div>
          </transition>

          <!-- Formulário de login -->
          <form @submit.prevent="handleLogin" class="space-y-6">
            <div class="space-y-2">
              <label for="username" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Usuário
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <input
                  id="username"
                  v-model="username"
                  type="text"
                  required
                  class="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-[#fbd5e5] focus:border-[#fbd5e5] bg-white dark:bg-gray-700 text-gray-900 dark:text-white sm:text-sm"
                  placeholder="Digite seu usuário ou email"
                />
              </div>
            </div>

            <div class="space-y-2">
              <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Senha
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="password"
                  required
                  class="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-[#fbd5e5] focus:border-[#fbd5e5] bg-white dark:bg-gray-700 text-gray-900 dark:text-white sm:text-sm"
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-[#fbd5e5] focus:outline-none"
                  aria-label="Mostrar/ocultar senha"
                >
                  <component :is="showPassword ? EyeOff : Eye" class="w-5 h-5" />
                </button>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <input
                  id="remember-me"
                  v-model="rememberMe"
                  type="checkbox"
                  class="h-4 w-4 text-[#fbd5e5] focus:ring-[#fbd5e5] border-gray-300 rounded"
                />
                <label for="remember-me" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Lembrar-me
                </label>
              </div>

              <button
                @click="handleForgotPassword"
                type="button"
                class="text-sm font-medium"
                :style="{ color: isDark ? clinicStore.clinic.cor_fonte_dark : clinicStore.clinic.cor_fonte }"
              >
                Esqueci minha senha
              </button>
            </div>

            <div>
              <button
                  type="submit"
                  class="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white transition-all duration-200 transform hover:-translate-y-1 login-btn"
                  :style="{ backgroundColor: 'var(--clinic-btn-bg)' }"
                :disabled="isLoading"
              >
                <span v-if="isLoading" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/>
                  </svg>
                  Aguarde...
                </span>
                <span v-else>Entrar</span>
              </button>
            </div>
          </form>

          <div class="mt-8 text-center">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Não possui conta? 
              <button 
                @click="handleRegister" 
                class="font-medium"
                :style="{ color: isDark ? clinicStore.clinic.cor_fonte_dark : clinicStore.clinic.cor_fonte }"
              >
                Solicite acesso
              </button>
            </p>
          </div>
        
        </div>
      </div>

      
      <!-- Footer -->
      <p class="text-center text-sm mt-8 text-gray-500 dark:text-gray-400">
        <a href="https://www.inova.med.br" target="_blank" rel="noopener" class="hover:underline font-medium">
          Desenvolvido por Tecnologia da Informação - CCDTI © Todos os direitos reservados
        </a>
      </p>
    </div>

    <!-- Modal de Versão -->
    <transition name="modal-fade">
      <div v-if="showVersionModal" class="fixed inset-0 z-50 flex items-center justify-center">
        <!-- Overlay -->
        <div 
          @click="closeVersionModal"
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        ></div>
        
        <!-- Modal -->
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200 dark:border-gray-700">
          <!-- Botão de fechar -->
          <button
            @click="closeVersionModal"
            class="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
            aria-label="Fechar modal"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <!-- Conteúdo do modal -->
          <div class="text-center">
            <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/50 mb-6">
              <svg class="h-8 w-8 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Informações da Versão
            </h3>
            
            <div class="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <div class="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span class="font-medium">Aplicação:</span>
                <span class="text-gray-800 dark:text-gray-200">Inovamed</span>
              </div>
              
              <div class="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span class="font-medium">Versão:</span>
                <span class="text-gray-800 dark:text-gray-200">{{ appVersion }}</span>
              </div>
            
            </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-6">
                Desenvolvido por Tecnologia da Informação - CCDTI © Todos os direitos reservados
              </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { login } from "@/services/authService"
import { Eye, EyeOff, Info } from 'lucide-vue-next'
import { useClinicStore } from '@/stores/settings/clinicStore'

const router = useRouter()
const waveCanvas = ref(null)
const clinicStore = useClinicStore()

// Estado do formulário
const showPassword = ref(false)
const username = ref('')
const password = ref('')
const rememberMe = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isDark = ref('')

// Estado de carregamento das cores da clínica
const isClinicLoading = ref(true)

// Estado do modal de versão
const showVersionModal = ref(false)

// Informações da versão
const appVersion = ref('1.2.0')
const buildDate = ref('')

// Função para fechar o modal
const closeVersionModal = () => {
  showVersionModal.value = false
}

// Função para obter a versão da aplicação
const getAppVersion = () => {
  // Tenta obter a versão do package.json ou usa uma versão padrão
  try {
    // Em um ambiente de produção, você pode querer usar uma variável de ambiente
    // ou um arquivo de configuração separado
    return import.meta.env.VITE_APP_VERSION || '1.2.0'
  } catch (error) {
    return '1.2.0'
  }
}

// Variáveis da animação de ondas
let ctx
let width
let height
let waves = []
let animationFrameId
let lastTime = 0

// Inicializa a animação de ondas
const initWaveAnimation = () => {
  if (!waveCanvas.value) return
  
  ctx = waveCanvas.value.getContext('2d')
  resizeCanvas()
  
  // Cria ondas com diferentes propriedades
  const waveColor1 = document.documentElement.classList.contains('dark')
    ? clinicStore.clinic.cor_wave_login_dark || '#4d5b6a'
    : clinicStore.clinic.cor_wave_login || '#fbd5e5';
  const waveColor2 = document.documentElement.classList.contains('dark')
    ? clinicStore.clinic.cor_wave_login_secundaria_dark || '#b48bbd'
    : clinicStore.clinic.cor_wave_login_secundaria || '#e9b6c9';
  waves = [
    createWave(0.02, 0.1, 4, waveColor1, 0.3),
    createWave(0.03, 0.15, 2, waveColor2, 0.2),
    createWave(0.01, 0.05, 3, waveColor1, 0.4),
    createWave(0.015, 0.07, 2.5, waveColor2, 0.2)
  ]
  
  // Inicia o loop de animação
  animateWaves()
  
  // Manipula redimensionamento da janela
  window.addEventListener('resize', resizeCanvas)
}

// Cria uma onda com propriedades específicas
const createWave = (frequency, amplitude, speed, color, opacity) => {
  return { frequency, amplitude, speed, color, opacity, offset: 0 }
}

// Redimensiona o canvas para corresponder ao tamanho da janela
const resizeCanvas = () => {
  if (!waveCanvas.value) return
  
  width = window.innerWidth
  height = window.innerHeight
  
  waveCanvas.value.width = width
  waveCanvas.value.height = height
}

// Anima as ondas
const animateWaves = (currentTime = 0) => {
  const deltaTime = currentTime - lastTime
  lastTime = currentTime
  
  // Limpa o canvas
  ctx.clearRect(0, 0, width, height)
  
  // Desenha cada onda
  waves.forEach(wave => {
    drawWave(wave, deltaTime)
  })
  
  // Continua o loop de animação
  animationFrameId = requestAnimationFrame(animateWaves)
}

// Desenha uma única onda
const drawWave = (wave, deltaTime) => {
  const { frequency, amplitude, speed, color, opacity, offset } = wave
  
  // Atualiza o offset da onda com base na velocidade
  wave.offset += speed * 0.001 * deltaTime
  
  ctx.beginPath()
  ctx.moveTo(0, height / 2)
  
  // Desenha o caminho da onda
  for (let x = 0; x < width; x++) {
    const y = Math.sin(x * frequency + offset) * amplitude * height / 2 + height / 2
    ctx.lineTo(x, y)
  }
  
  // Completa o caminho para preencher a onda
  ctx.lineTo(width, height)
  ctx.lineTo(0, height)
  ctx.closePath()
  
  // Define a cor e opacidade da onda
  ctx.fillStyle = color
  ctx.globalAlpha = opacity
  ctx.fill()
  ctx.globalAlpha = 1
}

// Atualiza as cores das ondas quando o tema muda
watch(isDark, () => {
  if (waves.length) {
    const waveColor1 = isDark.value
      ? clinicStore.clinic.cor_wave_login_dark || '#4d5b6a'
      : clinicStore.clinic.cor_wave_login || '#fbd5e5';
    const waveColor2 = isDark.value
      ? clinicStore.clinic.cor_wave_login_secundaria_dark || '#b48bbd'
      : clinicStore.clinic.cor_wave_login_secundaria || '#e9b6c9';
    waves[0].color = waveColor1;
    waves[1].color = waveColor2;
    waves[2].color = waveColor1;
    waves[3].color = waveColor2;
  }
})

// Alterna o modo escuro
const toggleDarkMode = () => {
  isDark.value = !isDark.value
  localStorage.setItem('darkMode', isDark.value)
  document.documentElement.classList.toggle('dark', isDark.value)
}

// Manipula o envio do formulário
const handleLogin = async () => {
  if (!username.value || !password.value) {
    errorMessage.value = "Por favor, preencha todos os campos"
    return
  }

  isLoading.value = true
  errorMessage.value = ""

  try {
    const user = await login(username.value, password.value)
    
    // Adiciona uma mensagem de sucesso antes de redirecionar
    successMessage.value = "Login realizado com sucesso!"
    
    // Pequeno delay para mostrar a mensagem de sucesso
    setTimeout(() => {
      // Se o usuário pertence ao grupo monitor, o redirecionamento já foi feito no authService
      if (user.groups && user.groups.includes('monitor')) {
        return;
      }
      // Caso contrário, redireciona para home
      router.push("/home")
    }, 1000)
  } catch (error) {
    errorMessage.value = "Usuário ou senha inválidos"
  } finally {
    isLoading.value = false
  }
}

// Manipuladores de navegação
const handleForgotPassword = () => {
  router.push({ name: 'ResetPassword' })
}

const handleRegister = () => {
  router.push({ name: 'Register' })
}

// Listener para tecla ESC
const handleEscKey = (event) => {
  if (event.key === 'Escape' && showVersionModal.value) {
    closeVersionModal()
  }
}

// Ciclo de vida
onMounted(async () => {
  isClinicLoading.value = true
  // Busca as cores da clínica antes de renderizar
  await clinicStore.fetchClinicStyles();
  isClinicLoading.value = false

  // Inicializa o modo escuro
  isDark.value = localStorage.getItem('darkMode') === 'true'
  document.documentElement.classList.toggle('dark', isDark.value)
  
  // Inicializa a animação de ondas
  initWaveAnimation()
  
  // Define a versão da aplicação
  appVersion.value = getAppVersion()
  
  // Define a data de build
  buildDate.value = new Date().toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
  
  // Verifica o parâmetro de consulta de acesso negado
  if (router.currentRoute.value.query.accessDenied) {
    errorMessage.value = 'Acesso negado. Por favor, faça login para continuar.'
    router.replace({ query: {} })
  }
  
  // Adiciona listener para tecla ESC
  document.addEventListener('keydown', handleEscKey)
})

onUnmounted(() => {
  // Limpa a animação
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
  
  // Remove os event listeners
  window.removeEventListener('resize', resizeCanvas)
  document.removeEventListener('keydown', handleEscKey)
})

// Logo do login baseada nas configurações da clínica
const loginLogoSrc = computed(() => {
  const logoPath = clinicStore.clinic.login_logo || '/styles/public/logo_login.png';
  return `${logoPath}?${Date.now()}`;
})
</script>

<style scoped>
/* Estilos base */
.dark {
  color-scheme: dark;
}

/* Animações */
.rotate-enter-active,
.rotate-leave-active {
  transition: transform 0.3s ease;
}
.rotate-enter-from,
.rotate-leave-to {
  transform: rotate(180deg);
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}
.message-fade-enter-from,
.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Animações do modal */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Message boxes */
.message-box {
  @apply flex items-center p-4 rounded-xl text-sm shadow-md;
}

.message-box.error {
  @apply bg-red-100 border-l-4 border-red-500 text-red-700 dark:bg-red-900/70 dark:border-red-700 dark:text-red-300;
}

.message-box.success {
  @apply bg-green-100 border-l-4 border-green-500 text-green-700 dark:bg-green-900/70 dark:border-green-700 dark:text-green-300;
}

/* Padrão de fundo para o lado esquerdo */
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Animação de hover para os feature items */
.feature-item {
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Animação do botão de tema */
@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
}

.theme-toggle:hover {
  animation: float 2s ease-in-out infinite;
}

/* Responsividade */
@media (max-width: 768px) {
  .feature-item {
    padding: 0.75rem;
  }
  
  .feature-icon-wrapper {
    padding: 0.5rem;
  }
}

/* Remove o botão nativo de mostrar senha */
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none;
}

input[type="password"]::-webkit-credentials-auto-fill-button,
input[type="password"]::-webkit-clear-button {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none;
  height: 0;
  width: 0;
  margin: 0;
  padding: 0;
}

.gradient-text {
  background: linear-gradient(90deg, #a83279, #fd79ac, #fa84b5, #a83279);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
}

.gradient-text-btn {
  background: linear-gradient(90deg, #a83279, #fd89b6, #ff7eb4, #a83279);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  font-weight: 600;
}

.login-screen .btn-primary {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
}

.login-logo img {
  max-height: 80px;
}

.login-btn:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
}
</style>